import { Link } from 'react-router-dom';
import { ArrowLeft, Users, Home, Monitor, Wrench, ClipboardList, Settings } from 'lucide-react';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-background py-12" data-testid="privacy-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Home
        </Link>

        <div className="bg-white rounded-xl border border-stone-200 p-8 md:p-12">
          <h1 className="font-heading text-3xl md:text-4xl font-bold mb-2">
            Privacy Policy
          </h1>
          <p className="text-muted-foreground mb-8">
            for the Healthy Homes NZ Tradie Portal
          </p>
          <p className="text-sm text-muted-foreground mb-8">Last Updated: February 2026</p>

          <div className="prose prose-stone max-w-none">
            <p className="text-foreground mb-6">
              Healthy Homes NZ Limited ("we", "us", "our") is committed to protecting the privacy of both the 
              tradespeople and service providers ("Trade Partners") and the landlords, property managers, and 
              homeowners ("Clients") who use the Healthy Homes NZ Tradie Portal (accessible at tradies.healthyhomesnz.co.nz).
            </p>
            <p className="text-foreground mb-8">
              This Privacy Policy explains how we collect, use, disclose, and protect your personal information 
              in accordance with the New Zealand Privacy Act 2020.
            </p>

            {/* Section 1 */}
            <h2 className="font-heading text-xl font-semibold mt-8 mb-4 text-primary">
              1. Information We Collect
            </h2>
            <p className="text-foreground mb-4">
              We collect information directly from you when you register an account, browse the portal, or 
              interact with our services.
            </p>

            <div className="bg-stone-50 rounded-lg p-6 my-6">
              <h3 className="font-heading text-lg font-medium mb-3 flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                For Trade Partners (Tradespeople & Service Providers)
              </h3>
              <ul className="space-y-3 text-foreground">
                <li>
                  <strong>Account & Profile Details:</strong> Your name, business name, physical or postal address, 
                  phone number, email address, and website link.
                </li>
                <li>
                  <strong>Trade Details:</strong> Your trade type, services offered, locations serviced, and any 
                  trade qualifications or licence numbers you provide.
                </li>
                <li>
                  <strong>Subscription Payment Information:</strong> Billing details required for your monthly 
                  subscription. All transactions are securely processed via our third-party provider, Stripe. 
                  We do not store or view your full credit card details.
                </li>
              </ul>
            </div>

            <div className="bg-stone-50 rounded-lg p-6 my-6">
              <h3 className="font-heading text-lg font-medium mb-3 flex items-center gap-2">
                <Home className="w-5 h-5 text-primary" />
                For Clients (Landlords, Property Managers & Homeowners)
              </h3>
              <ul className="space-y-3 text-foreground">
                <li>
                  <strong>Contact Details:</strong> Your name, email address, phone number, and the physical 
                  address of the property requiring maintenance.
                </li>
                <li>
                  <strong>Inspection Details:</strong> Specific data regarding the areas where your property 
                  did not meet the Healthy Homes Standards.
                </li>
              </ul>
            </div>

            <div className="bg-stone-50 rounded-lg p-6 my-6">
              <h3 className="font-heading text-lg font-medium mb-3 flex items-center gap-2">
                <Monitor className="w-5 h-5 text-primary" />
                General Technical Data (Both Trade Partners & Clients)
              </h3>
              <ul className="space-y-3 text-foreground">
                <li>
                  <strong>Usage Data:</strong> Technical information such as your IP address, browser type, 
                  and how you interact with the Healthy Homes NZ Tradie Portal.
                </li>
              </ul>
            </div>

            {/* Section 2 */}
            <h2 className="font-heading text-xl font-semibold mt-10 mb-4 text-primary">
              2. How We Use Your Information
            </h2>
            <p className="text-foreground mb-4">
              We use the collected data to operate the referral platform and connect both parties effectively.
            </p>

            <div className="bg-stone-50 rounded-lg p-6 my-6">
              <h3 className="font-heading text-lg font-medium mb-3 flex items-center gap-2">
                <Wrench className="w-5 h-5 text-primary" />
                To Support Trade Partners
              </h3>
              <ul className="space-y-3 text-foreground">
                <li>Publish your business profile in the public portal directory so Clients can find and contact you directly.</li>
                <li>Manage your monthly subscription, process payments via Stripe, and send you billing receipts.</li>
                <li>Verify your professional credentials to maintain the quality of the listings.</li>
              </ul>
            </div>

            <div className="bg-stone-50 rounded-lg p-6 my-6">
              <h3 className="font-heading text-lg font-medium mb-3 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-primary" />
                To Support Clients
              </h3>
              <ul className="space-y-3 text-foreground">
                <li>Facilitate connections between you and local tradespeople capable of making your property compliant.</li>
                <li>Generate direct recommendations based on the specific failed items in your Healthy Homes inspection report.</li>
              </ul>
            </div>

            <div className="bg-stone-50 rounded-lg p-6 my-6">
              <h3 className="font-heading text-lg font-medium mb-3 flex items-center gap-2">
                <Settings className="w-5 h-5 text-primary" />
                General Operations (Both Parties)
              </h3>
              <ul className="space-y-3 text-foreground">
                <li>Communicate important updates regarding your account, maintenance windows, or portal changes.</li>
                <li>Monitor traffic and improve usability to make the directory faster and more intuitive.</li>
              </ul>
            </div>

            {/* Section 3 */}
            <h2 className="font-heading text-xl font-semibold mt-10 mb-4 text-primary">
              3. How We Share Your Information
            </h2>
            <p className="text-foreground mb-4">
              We do not sell, rent, or lease your personal information to third parties. We only share 
              information to enable the referral services:
            </p>
            <ul className="space-y-4 text-foreground mb-6">
              <li>
                <strong>Public Directory Listing:</strong> Trade Partner contact details (business name, phone, 
                email, and locations) are publicly displayed on the portal so Clients can reach out directly.
              </li>
              <li>
                <strong>Job Inquiries:</strong> If a Client uses the portal to contact a tradesperson, the 
                Client's contact details and specific repair requirements are shared directly with that Trade Partner.
              </li>
              <li>
                <strong>Third-Party Service Providers:</strong> We share relevant data with trusted vendors—such 
                as Stripe for payment processing and our website hosting providers—who help us run our platform. 
                All providers are bound by strict confidentiality terms.
              </li>
              <li>
                <strong>Legal Requirements:</strong> We may disclose your information if required to do so by 
                New Zealand law or to protect the safety and rights of our users.
              </li>
            </ul>

            {/* Section 4 */}
            <h2 className="font-heading text-xl font-semibold mt-10 mb-4 text-primary">
              4. Data Security and Storage
            </h2>
            <p className="text-foreground mb-4">
              <strong>4.1 Cloud Security:</strong> We store your personal information electronically using secure 
              cloud servers with restricted access and industry-standard security measures (such as SSL encryption).
            </p>
            <p className="text-foreground mb-6">
              <strong>4.2 Payment Security:</strong> All financial transactions are processed securely through 
              Stripe's external network. Healthy Homes NZ Limited does not store complete billing credentials 
              on our own servers.
            </p>

            {/* Section 5 */}
            <h2 className="font-heading text-xl font-semibold mt-10 mb-4 text-primary">
              5. Cookies and Analytics
            </h2>
            <p className="text-foreground mb-4">
              We use Google Analytics 4 to understand how visitors interact with the Healthy Homes NZ
              Tradies Directory so we can improve the experience. Google Analytics uses cookies and
              similar technologies to collect:
            </p>
            <ul className="space-y-3 text-foreground mb-4">
              <li>Pages viewed and time spent on each page</li>
              <li>How you arrived at our site (e.g., search engine, direct link, referral)</li>
              <li>Approximate location (country/region only — IPs are anonymised)</li>
              <li>Anonymous interactions, such as which tradies are contacted and which search filters are used</li>
            </ul>
            <p className="text-foreground mb-4">
              This data is aggregated and does not personally identify you. We use it solely to measure
              traffic and improve the platform — not for advertising. Google's use of this data is governed
              by their{' '}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Privacy Policy
              </a>.
            </p>
            <p className="text-foreground mb-6">
              You can opt out of Google Analytics tracking by installing the official{' '}
              <a
                href="https://tools.google.com/dlpage/gaoptout"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Google Analytics Opt-out Browser Add-on
              </a>, or by enabling "Do Not Track" / private-browsing mode in your browser.
            </p>

            {/* Section 6 */}
            <h2 className="font-heading text-xl font-semibold mt-10 mb-4 text-primary">
              6. Your Rights Under the Privacy Act 2020
            </h2>
            <p className="text-foreground mb-4">
              No matter if you are a Trade Partner or a Client, you have the right to:
            </p>
            <ul className="space-y-3 text-foreground mb-6">
              <li><strong>Access</strong> any personal information we hold about you.</li>
              <li><strong>Correct</strong> any errors or update your details. Trade Partners can update profile 
              information directly through their account dashboard.</li>
              <li><strong>Request deletion</strong> of your information. While we will remove your public listing 
              or details, certain data may be retained for New Zealand tax and auditing purposes.</li>
            </ul>
            <p className="text-foreground mb-6">
              To exercise these rights, please email us directly at{' '}
              <a href="mailto:privacy@healthyhomesnz.co.nz" className="text-primary hover:underline">
                privacy@healthyhomesnz.co.nz
              </a>.
            </p>

            {/* Section 7 */}
            <h2 className="font-heading text-xl font-semibold mt-10 mb-4 text-primary">
              7. Changes to This Privacy Policy
            </h2>
            <p className="text-foreground mb-4">
              We may update this Privacy Policy from time to time to reflect changes in our practices or 
              New Zealand privacy laws. Any significant updates will be communicated via email or posted 
              directly on the Healthy Homes NZ Tradie Portal.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
